import { useDynamicFormContext } from "@/components/DynamicForm/DynamicFormProvider";
import { EntityStatus } from "@/enums";
import { selectFilterHelper } from "@/helpers";
import { Select, Typography } from "antd";
import { bool, object } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { DynamicFormItem } from "./DynamicFormItem";
import { getPlaceholderText } from "./helper/getPlaceholderText";
import { useCheckEditableFormField } from "./hooks/useCheckEditableFormField";
import * as S from "./styled";

const { Option } = Select;
const { Text } = Typography;

const DynamicDropDown = ({
  formField,
  formDisabled,
  defaultOpen = false,
}) => {
  const intl = useIntl();
  const [options, setOptions] = useState([]);
  const [originalValue, setOriginalValue] = useState(null);
  const {
    isDisabled,
    formConfig,
    formData,
    formRef,
    getDependentPropertyValue,
    getDependentPropertyField,
    handleValuesChange,
  } = useCheckEditableFormField(formField);
  const { flatForms } = useDynamicFormContext();

  useEffect(() => {
    if (!formData || !formField) return;

    let changedValue = null;
    const currentFormFieldOptionValue = formData[formField.property.name];

    // Gets the configuration for the option // the currently selected option
    const currentOptionConfig = formField.values.find(
      ({ id }) => id === currentFormFieldOptionValue
    );

    if (currentOptionConfig) {
      const { dependsOnPropertyValues } = currentOptionConfig;

      const dependsOnFormFieldId = formField.property.dependsOnPropertyId;
      if (dependsOnFormFieldId) {
        const dependsOnFormField =
          getDependentPropertyField(dependsOnFormFieldId);

        let dependsOnFieldValue = formRef.current.getFieldValue(
          dependsOnFormField.property.name
        );
        // check if dependent value is on parent form
        if (
          !dependsOnFieldValue &&
          formField.property.target !== dependsOnFormField.property.target
        ) {
          dependsOnFieldValue = getDependentPropertyValue(dependsOnFormFieldId);
        }

        if (
          dependsOnPropertyValues?.length &&
          !dependsOnPropertyValues.includes(dependsOnFieldValue)
        ) {
          changedValue = { [formField.property.name]: "" };
        }
      }
    }

    if (changedValue) {
      formRef.current.setFieldsValue(changedValue);
    }
  }, [
    formData,
    formField,
    formRef,
    getDependentPropertyField,
    getDependentPropertyValue,
  ]);

  useEffect(() => {
    if (originalValue) return;

    const value = formRef?.current?.getFieldValue(formField?.property?.name);

    if (!value) return;

    setOriginalValue(value);
  }, [formField, formRef, originalValue]);

  /***
   * Whenever a value on the form configuration changes, we need to recalculate
   * and form dependencies we may or may not have.
   */
  useEffect(() => {
    const formOptions = formField.values
      ?.filter(
        // Assumption: If current id exists but is inactive now, we should still populate the dropdown even if it's not an option anymore.
        ({ status, id }) =>
          status === EntityStatus.Active || originalValue === id
      )
      ?.map((option) => option);

    const dropDownOptions = [];

    //Check for dependencies.  If they exist, check against current value and verify that they're allowable.  Else simply add the item to the list.
    if (!flatForms) return;

    formOptions.forEach((option) => {
      const { dependsOnPropertyValues } = option;

      if (!dependsOnPropertyValues?.length) {
        dropDownOptions.push(option);
        return;
      }

      dependsOnPropertyValues.forEach((dependentValue) => {
        flatForms.forEach((field) => {
          if (!field.values.find((value) => value.id === dependentValue))
            return;

          const value = formRef?.current?.getFieldValue(field.property.name);

          if (value !== dependentValue) return;

          dropDownOptions.push(option);
        });
      });
    });

    setOptions(dropDownOptions);
  }, [
    formConfig,
    formData,
    formField,
    formRef,
    getDependentPropertyValue,
    originalValue,
    flatForms,
  ]);

  useEffect(() => {
    const { name } = formField.property;
    const curValue = formRef.current.getFieldValue(name);
    let change = null;

    if (options?.length === 1) {
      if (!curValue || curValue !== options[0].id) {
        change = {
          [name]: options[0].id,
        };
      }
    }
    if (change) {
      formRef.current.setFieldsValue(change);
      handleValuesChange(change);
    }
    //if we include the other items, this will incorrectly set the value before the options are actually updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef, options]);

  return (
    <DynamicFormItem formFieldProperty={formField.property}>
      <S.DynamicSelect
        $width={
          formField.property.fieldType === "MultiSelectDropDown"
            ? "100%"
            : undefined
        }
        data-test-id={`dynamic-dropdown-${formField.property.name}`}
        mode={
          formField.property.fieldType === "MultiSelectDropDown"
            ? "multiple"
            : undefined
        }
        placeholder={getPlaceholderText(formDisabled, formField, intl)}
        filterOption={selectFilterHelper}
        disabled={formDisabled || isDisabled}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        allowClear
        defaultOpen={defaultOpen}
        showSearch={true}
      >
        {options
          .sort(({ order: a }, { order: b }) => a - b)
          .map(({ id, label, status }) => (
            <Option key={`dropdown-${formField.order}-${id}`} value={id}>
              {status === "Active" ? (
                intl.formatMessage({
                  id: label.id,
                  defaultMessage: label.fallback,
                })
              ) : (
                <Text type="secondary">
                  {intl.formatMessage({
                    id: label.id,
                    defaultMessage: label.fallback,
                  })}
                </Text>
              )}
            </Option>
          ))}
      </S.DynamicSelect>
    </DynamicFormItem>
  );
};

DynamicDropDown.propTypes = {
  formField: object.isRequired,
  formDisabled: bool,
  defaultOpen: bool,
};

export { DynamicDropDown };
